import React from 'react';
import { Link } from 'react-router-dom';
import { useCart } from '../../context/CartContext';
import { X, Plus, Minus, ShoppingCart, Trash2, ArrowRight, Eye, Truck } from 'lucide-react';
import { categories, SHIPPING_METHOD, FREE_SHIPPING_THRESHOLD } from '../../data/mock';

const CartDrawer = () => {
  const { 
    isCartOpen, 
    closeCart, 
    cartItems, 
    cartTotal, 
    updateQuantity, 
    removeFromCart 
  } = useCart();

  if (!isCartOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-neutral-900/60 backdrop-blur-sm z-50"
        onClick={closeCart}
      />
      
      {/* Drawer */}
      <div className="fixed top-0 right-0 h-full w-full max-w-md bg-white z-50 shadow-2xl flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-neutral-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-neutral-900 rounded-xl flex items-center justify-center">
              <ShoppingCart className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="font-display text-lg text-neutral-900 font-semibold">Shopping Cart</h2>
              <p className="text-sm text-neutral-500">{cartItems.length} {cartItems.length === 1 ? 'item' : 'items'}</p>
            </div>
          </div>
          <button 
            onClick={closeCart}
            className="p-2.5 hover:bg-neutral-100 rounded-xl transition-colors"
          >
            <X className="w-5 h-5 text-neutral-500" />
          </button>
        </div>

        {/* Cart Items */}
        <div className="flex-1 overflow-y-auto p-5 bg-neutral-50">
          {cartItems.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <div className="w-20 h-20 bg-neutral-200 rounded-2xl flex items-center justify-center mb-5">
                <Eye className="w-10 h-10 text-neutral-400" />
              </div>
              <h3 className="font-medium text-neutral-900 text-lg mb-2">Your cart is empty</h3>
              <p className="text-neutral-500 text-sm mb-6">Browse our eyewear collection</p>
              <button 
                onClick={closeCart}
                className="px-6 py-3 bg-neutral-900 hover:bg-neutral-800 text-white font-medium rounded-full transition-colors"
              >
                Continue Shopping
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {cartItems.map(item => {
                const itemCategory = categories.find(c => c.id === item.categoryId);
                return (
                  <div key={item.id} className="flex gap-4 p-4 bg-white rounded-xl">
                    {/* Image */}
                    <div className="w-20 h-20 bg-neutral-100 rounded-xl overflow-hidden flex-shrink-0">
                      <img 
                        src={item.image} 
                        alt={item.name}
                        className="w-full h-full object-contain p-2"
                      />
                    </div>
                    
                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-neutral-400 uppercase tracking-wider font-medium mb-1">
                        {itemCategory?.name || 'Eyewear'}
                      </p>
                      <h4 className="font-medium text-neutral-900 text-sm mb-1.5 line-clamp-2">
                        {item.name}
                      </h4>
                      <p className="text-neutral-900 font-semibold">
                        ${item.price.toFixed(2)}
                      </p>
                      
                      {/* Quantity Controls */}
                      <div className="flex items-center justify-between mt-3">
                        <div className="flex items-center bg-neutral-100 rounded-full overflow-hidden">
                          <button 
                            onClick={() => updateQuantity(item.id, item.quantity - 1)}
                            className="p-2 hover:bg-neutral-200 transition-colors"
                          >
                            <Minus className="w-3 h-3 text-neutral-600" />
                          </button>
                          <span className="w-8 text-center text-sm font-medium text-neutral-900">
                            {item.quantity}
                          </span>
                          <button 
                            onClick={() => updateQuantity(item.id, item.quantity + 1)}
                            className="p-2 hover:bg-neutral-200 transition-colors"
                          >
                            <Plus className="w-3 h-3 text-neutral-600" />
                          </button>
                        </div>
                        <button 
                          onClick={() => removeFromCart(item.id)}
                          className="p-2 text-neutral-400 hover:text-red-500 hover:bg-neutral-100 rounded-lg transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        {cartItems.length > 0 && (
          <div className="p-5 border-t border-neutral-100 bg-white space-y-4">
            {/* Free Shipping Progress */}
            {cartTotal < FREE_SHIPPING_THRESHOLD && (
              <div className="bg-neutral-50 rounded-xl p-4">
                <div className="flex items-center justify-between text-sm mb-2">
                  <span className="text-neutral-600 flex items-center gap-2">
                    <Truck className="w-4 h-4" />
                    Add ${(FREE_SHIPPING_THRESHOLD - cartTotal).toFixed(2)} more for FREE shipping
                  </span>
                </div>
                <div className="w-full bg-neutral-200 rounded-full h-1.5 overflow-hidden">
                  <div 
                    className="h-full bg-neutral-900 rounded-full transition-all duration-300"
                    style={{ width: `${Math.min((cartTotal / FREE_SHIPPING_THRESHOLD) * 100, 100)}%` }}
                  />
                </div>
              </div>
            )}
            {cartTotal >= FREE_SHIPPING_THRESHOLD && (
              <div className="flex items-center gap-2 px-4 py-3 bg-green-50 rounded-xl">
                <Truck className="w-5 h-5 text-green-600" />
                <span className="text-green-700 text-sm font-medium">You've unlocked FREE shipping!</span>
              </div>
            )}

            {/* Subtotal */}
            <div className="flex items-center justify-between">
              <span className="text-neutral-600 font-medium">Subtotal</span>
              <span className="text-2xl font-semibold text-neutral-900">${cartTotal.toFixed(2)}</span>
            </div>

            {/* Checkout Button */}
            <Link
              to="/checkout"
              onClick={closeCart}
              className="flex items-center justify-center gap-2 w-full py-4 bg-neutral-900 hover:bg-neutral-800 text-white font-medium rounded-full transition-colors"
            >
              Checkout
              <ArrowRight className="w-5 h-5" />
            </Link>

            {/* Continue Shopping */}
            <button 
              onClick={closeCart}
              className="w-full text-center text-neutral-500 hover:text-neutral-900 py-2 text-sm font-medium transition-colors"
            >
              Continue Shopping
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default CartDrawer;
